import type { ByteSpan } from './types';

/** ALG-002: validate before any offset-based scheduling. */
export function isValidSpan(s: ByteSpan): boolean {
  return (
    Number.isSafeInteger(s.start) && Number.isSafeInteger(s.end) && s.start >= 0 && s.end > s.start
  );
}

export function span(start: number, end: number): ByteSpan {
  if (!Number.isSafeInteger(start) || !Number.isSafeInteger(end) || start < 0 || end <= start) {
    throw new RangeError(`invalid span [${start}, ${end})`);
  }
  return Object.freeze({ start, end });
}

export function spanLength(s: ByteSpan): number {
  return s.end - s.start;
}

/** Convert a half-open span to an HTTP Range header at the network boundary. ALG-001 */
export function toRangeHeader(s: ByteSpan): string {
  return `bytes=${s.start}-${s.end - 1}`;
}

/**
 * A normalized set of disjoint, non-touching, ascending half-open spans.
 * Immutable: every mutator returns a new set. ALG-003
 */
export class IntervalSet {
  private constructor(private readonly items: readonly ByteSpan[]) {}

  static readonly empty = new IntervalSet([]);

  static from(spans: Iterable<ByteSpan>): IntervalSet {
    const sorted = [...spans]
      .filter(isValidSpan)
      .sort((a, b) => a.start - b.start || a.end - b.end);
    const out: ByteSpan[] = [];
    for (const s of sorted) {
      const last = out[out.length - 1];
      if (last && s.start <= last.end) {
        if (s.end > last.end) out[out.length - 1] = { start: last.start, end: s.end };
      } else {
        out.push({ start: s.start, end: s.end });
      }
    }
    return new IntervalSet(out);
  }

  get spans(): readonly ByteSpan[] {
    return this.items;
  }

  get isEmpty(): boolean {
    return this.items.length === 0;
  }

  /** Total number of bytes covered. */
  measure(): number {
    let n = 0;
    for (const s of this.items) n += s.end - s.start;
    return n;
  }

  add(s: ByteSpan): IntervalSet {
    if (!isValidSpan(s)) return this;
    return IntervalSet.from([...this.items, s]);
  }

  union(other: IntervalSet): IntervalSet {
    return IntervalSet.from([...this.items, ...other.items]);
  }

  /** True if every byte in `s` is covered. */
  covers(s: ByteSpan): boolean {
    if (!isValidSpan(s)) return false;
    const i = this.findContaining(s.start);
    const it = i >= 0 ? this.items[i] : undefined;
    return it !== undefined && it.end >= s.end;
  }

  /** Does the byte at `pos` belong to the set? */
  has(pos: number): boolean {
    return this.findContaining(pos) >= 0;
  }

  /** Spans of `s` not covered by this set, ascending. ALG-004 */
  missing(s: ByteSpan): ByteSpan[] {
    if (!isValidSpan(s)) return [];
    const holes: ByteSpan[] = [];
    let cursor = s.start;
    for (const it of this.items) {
      if (it.end <= cursor) continue;
      if (it.start >= s.end) break;
      if (it.start > cursor) holes.push({ start: cursor, end: Math.min(it.start, s.end) });
      cursor = Math.max(cursor, it.end);
      if (cursor >= s.end) break;
    }
    if (cursor < s.end) holes.push({ start: cursor, end: s.end });
    return holes;
  }

  /** Parts of `s` that ARE covered. */
  intersect(s: ByteSpan): ByteSpan[] {
    if (!isValidSpan(s)) return [];
    const out: ByteSpan[] = [];
    for (const it of this.items) {
      if (it.end <= s.start) continue;
      if (it.start >= s.end) break;
      out.push({ start: Math.max(it.start, s.start), end: Math.min(it.end, s.end) });
    }
    return out;
  }

  subtract(other: IntervalSet): IntervalSet {
    let result: ByteSpan[] = [...this.items];
    for (const o of other.items) {
      const next: ByteSpan[] = [];
      for (const r of result) {
        if (o.end <= r.start || o.start >= r.end) {
          next.push(r);
          continue;
        }
        if (o.start > r.start) next.push({ start: r.start, end: o.start });
        if (o.end < r.end) next.push({ start: o.end, end: r.end });
      }
      result = next;
    }
    return new IntervalSet(result);
  }

  /** First hole in [from, limit), if any. */
  firstHole(from: number, limit: number): ByteSpan | null {
    if (limit <= from) return null;
    const m = this.missing({ start: from, end: limit });
    return m[0] ?? null;
  }

  /** First covered byte >= pos, used to bound a hole. */
  nextCoveredStart(pos: number): number | null {
    for (const it of this.items) {
      if (it.end <= pos) continue;
      return Math.max(it.start, pos);
    }
    return null;
  }

  /** Length of the contiguous covered run starting at `pos` (0 if pos is a hole). */
  contiguousFrom(pos: number): number {
    const i = this.findContaining(pos);
    const it = i >= 0 ? this.items[i] : undefined;
    return it ? it.end - pos : 0;
  }

  /** Clamp to [0, total). TYPE-019 */
  clampTo(total: number): IntervalSet {
    const out: ByteSpan[] = [];
    for (const s of this.items) {
      if (s.start >= total) break;
      out.push({ start: s.start, end: Math.min(s.end, total) });
    }
    return new IntervalSet(out);
  }

  /** True only when there are no holes in [0,total). TYPE-032 */
  isComplete(total: number): boolean {
    if (total === 0) return true;
    return this.items.length === 1 && this.items[0]!.start === 0 && this.items[0]!.end >= total;
  }

  toJSON(): ByteSpan[] {
    return this.items.map((s) => ({ start: s.start, end: s.end }));
  }

  private findContaining(pos: number): number {
    let lo = 0;
    let hi = this.items.length - 1;
    while (lo <= hi) {
      const mid = (lo + hi) >> 1;
      const it = this.items[mid]!;
      if (pos < it.start) hi = mid - 1;
      else if (pos >= it.end) lo = mid + 1;
      else return mid;
    }
    return -1;
  }
}

/** Merge spans separated by less than `gap` bytes, only when it won't read > maxWaste junk. ALG-030 */
export function coalesceSpans(spans: readonly ByteSpan[], gap: number): ByteSpan[] {
  const sorted = [...spans].filter(isValidSpan).sort((a, b) => a.start - b.start);
  const out: ByteSpan[] = [];
  for (const s of sorted) {
    const last = out[out.length - 1];
    if (last && s.start - last.end <= gap) {
      out[out.length - 1] = { start: last.start, end: Math.max(last.end, s.end) };
    } else {
      out.push({ start: s.start, end: s.end });
    }
  }
  return out;
}

/**
 * Down-sample a set of spans into `buckets` cells for drawing a coverage rail.
 * Each value is the fraction [0,1] of that cell that is covered.
 */
export function coverageBuckets(set: IntervalSet, total: number, buckets: number): Float32Array {
  const out = new Float32Array(buckets);
  if (total <= 0 || buckets <= 0) return out;
  const size = total / buckets;
  for (const s of set.spans) {
    let b = Math.floor(s.start / size);
    const last = Math.min(buckets - 1, Math.floor((s.end - 1) / size));
    for (; b <= last; b++) {
      const cs = b * size;
      const ce = cs + size;
      const overlap = Math.min(ce, s.end) - Math.max(cs, s.start);
      if (overlap > 0) out[b] = Math.min(1, (out[b] ?? 0) + overlap / size);
    }
  }
  return out;
}
